import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Alert, Divider, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TextField, MenuItem,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StatusBadge } from '../../components/StatusBadge';
import { FileUploadField } from '../../components/FileUploadField';
import { paymentsApi, type Payment } from '../../api/finance.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const ACTIONS: Record<string, { action: string; label: string; perm: string; color?: 'success' | 'error' }[]> = {
  draft: [{ action: 'submit', label: 'Submit', perm: 'payment.update' }, { action: 'cancel', label: 'Cancel', perm: 'payment.approve', color: 'error' }],
  pending_approval: [{ action: 'approve', label: 'Approve', perm: 'payment.approve', color: 'success' }, { action: 'reject', label: 'Reject', perm: 'payment.approve', color: 'error' }],
  approved: [{ action: 'verify', label: 'Finance Verify', perm: 'payment.verify', color: 'success' }, { action: 'reject', label: 'Reject', perm: 'payment.approve', color: 'error' }],
  finance_verified: [{ action: 'release', label: 'Release (UTR)', perm: 'payment.release', color: 'success' }, { action: 'reject', label: 'Reject', perm: 'payment.approve', color: 'error' }],
  released: [{ action: 'close', label: 'Close', perm: 'payment.update' }],
  closed: [], rejected: [], cancelled: [],
};

export function PaymentDetailDialog({ paymentId, onClose }: { paymentId: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = paymentId !== null;
  const [error, setError] = useState<string | null>(null);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [utr, setUtr] = useState('');
  const [paidDate, setPaidDate] = useState('');
  const [docType, setDocType] = useState('invoice');
  const [fileName, setFileName] = useState('');
  const [fileUrl, setFileUrl] = useState('');

  const pay = useQuery({ queryKey: ['payment', paymentId], queryFn: () => paymentsApi.get(paymentId as number), enabled: open });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['payment', paymentId] }); qc.invalidateQueries({ queryKey: ['payments'] }); qc.invalidateQueries({ queryKey: ['finance-dashboard'] }); };

  const statusMut = useMutation({
    mutationFn: (body: { action: string; utrNumber?: string; paidDate?: string }) => paymentsApi.changeStatus(paymentId as number, body),
    onSuccess: () => { setReleaseOpen(false); setUtr(''); setPaidDate(''); refresh(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const docMut = useMutation({
    mutationFn: () => paymentsApi.addDocument(paymentId as number, { docType, fileName, fileUrl }),
    onSuccess: () => { setFileName(''); setFileUrl(''); qc.invalidateQueries({ queryKey: ['payment', paymentId] }); },
    onError: (e) => setError(extractApiError(e)),
  });

  if (!open) return null;
  const d: Payment | undefined = pay.data;
  const actions = (ACTIONS[d?.status ?? ''] ?? []).filter((a) => hasPermission(a.perm));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{d?.code} <StatusBadge status={d?.status} /></DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack direction="row" spacing={3} flexWrap="wrap" mb={2}>
          <Typography variant="body2">Type: <b>{d?.paymentType}</b></Typography>
          <Typography variant="body2">Amount: <b>₹{(d?.totalAmount ?? 0).toLocaleString()}</b></Typography>
          <Typography variant="body2">{d?.partyType} #{d?.vendorId ?? d?.clientId}</Typography>
          {d?.utrNumber && <Chip size="small" color="success" label={`UTR ${d.utrNumber}`} />}
          {d?.paidDate && <Typography variant="body2">Paid: {d.paidDate}</Typography>}
        </Stack>

        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
          {actions.map((a) => (
            <Button key={a.action} size="small" variant="outlined" color={a.color}
              onClick={() => (a.action === 'release' ? setReleaseOpen(true) : statusMut.mutate({ action: a.action }))}>
              {a.label}
            </Button>
          ))}
        </Stack>

        <Divider sx={{ mb: 1 }} />
        <Typography variant="subtitle2" gutterBottom>Documents (invoice / WO / completion proof / approval)</Typography>
        {hasPermission('payment.update') && (
          <Stack direction="row" spacing={1} mb={1}>
            <TextField select size="small" label="Type" value={docType} onChange={(e) => setDocType(e.target.value)} sx={{ minWidth: 150 }}>
              {['invoice', 'work_order', 'completion_proof', 'approval', 'other'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField size="small" label="File name" value={fileName} onChange={(e) => setFileName(e.target.value)} />
            <FileUploadField label="Document" value={fileUrl || null} onChange={(url) => setFileUrl(url ?? '')} />
            <Button variant="contained" size="small" disabled={!fileName || !fileUrl} onClick={() => docMut.mutate()}>Add</Button>
          </Stack>
        )}
        <Table size="small">
          <TableHead><TableRow><TableCell>Type</TableCell><TableCell>File</TableCell><TableCell>Version</TableCell><TableCell>Current</TableCell></TableRow></TableHead>
          <TableBody>
            {(d?.documents ?? []).map((doc) => (
              <TableRow key={doc.id}><TableCell>{doc.docType}</TableCell><TableCell>{doc.fileName}</TableCell><TableCell>v{doc.version}</TableCell><TableCell>{doc.isCurrent ? <Chip size="small" color="success" label="current" /> : '—'}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>

      <Dialog open={releaseOpen} onClose={() => setReleaseOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Release Payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="UTR / Transaction Ref" value={utr} onChange={(e) => setUtr(e.target.value)} required fullWidth />
            <TextField label="Paid Date" type="date" InputLabelProps={{ shrink: true }} value={paidDate} onChange={(e) => setPaidDate(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!utr} onClick={() => statusMut.mutate({ action: 'release', utrNumber: utr, paidDate: paidDate || undefined })}>Release</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
